import type { MessageKey, MessageParameters } from "./catalog";
import { getLocale, translateMessage } from "./runtime";

type LabelKey = {
  [K in MessageKey]: keyof MessageParameters<K> extends never ? K : never;
}[MessageKey];
const labelKeys = new WeakMap<
  Record<string, string>,
  Record<string, LabelKey>
>();

/** Keep existing label lookups reactive while retaining each value's message key. */
export function createLabels<K extends string>(
  keys: Record<K, LabelKey>,
): Record<K, string> {
  const labels = Object.fromEntries(
    Object.entries(keys).map(([name, key]) => [name, key]),
  ) as Record<K, string>;
  for (const name of Object.keys(keys) as K[]) {
    Object.defineProperty(labels, name, {
      enumerable: true,
      get: () => translateMessage(getLocale(), keys[name] as LabelKey, []),
    });
  }
  labelKeys.set(labels, keys);
  return labels;
}

/** A fallback sentence uses labels in its own locale, before adding particles. */
export function labelInLocale(
  labels: Record<string, string>,
  name: string,
  locale: string,
): string | undefined {
  const keys = labelKeys.get(labels);
  const key = keys && Object.hasOwn(keys, name) ? keys[name] : undefined;
  return key
    ? translateMessage(locale, key, [])
    : Object.hasOwn(labels, name)
      ? labels[name]
      : undefined;
}
